import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SETTINGS } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

// Curated model catalog by provider. Emergent LLM key supports:
//   openai:  gpt-5.x family (text + gpt-image-1)
//   anthropic: claude sonnet/opus/haiku (text only)
//   gemini:    gemini 3.x family (text + nano banana image)
// Users may still type a custom model — this list only powers the picker.
const PROVIDER_MODELS = {
  openai: [
    { id: "gpt-5.6-terra",  label: "GPT-5.6 Terra (recommended)" },
    { id: "gpt-5.6-sol",    label: "GPT-5.6 Sol" },
    { id: "gpt-5.6-luna",   label: "GPT-5.6 Luna" },
    { id: "gpt-5.5",        label: "GPT-5.5" },
    { id: "gpt-5.4",        label: "GPT-5.4" },
    { id: "gpt-5.4-mini",   label: "GPT-5.4 Mini" },
  ],
  anthropic: [
    { id: "claude-sonnet-5",   label: "Claude Sonnet 5 (recommended)" },
    { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
    { id: "claude-opus-4-8",   label: "Claude Opus 4.8" },
    { id: "claude-opus-4-7",   label: "Claude Opus 4.7" },
    { id: "claude-opus-4-6",   label: "Claude Opus 4.6" },
    { id: "claude-haiku-4-5",  label: "Claude Haiku 4.5" },
  ],
  gemini: [
    { id: "gemini-3.1-pro",   label: "Gemini 3.1 Pro (recommended)" },
    { id: "gemini-3.5-flash", label: "Gemini 3.5 Flash" },
    { id: "gemini-3-flash",   label: "Gemini 3 Flash" },
  ],
};

const PROVIDER_DEFAULT_MODEL = {
  openai: "gpt-5.6-terra",
  anthropic: "claude-sonnet-5",
  gemini: "gemini-3.1-pro",
};
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { notifyBrokerChanged, resetExecutionSourceOverride } from "@/lib/executionSource";
import BrokerCockpit from "@/components/BrokerCockpit";
import { useUnsavedChanges, SaveBar, UnsavedChangesGuard, useGuardedTabsChange } from "@/lib/useUnsavedChanges";

export default function Settings() {
  const { user, refreshMe } = useAuth();
  const [tab, setTab] = useState("broker");
  const onValueChange = useGuardedTabsChange(setTab);
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid={SETTINGS.root}>
      <h1 className="text-xl font-medium tracking-tight">Settings</h1>
      <Tabs value={tab} onValueChange={onValueChange} className="w-full">
        <TabsList className="!rounded-none border-b border-border/60 bg-transparent p-0 h-auto">
          {["broker","risk","mfa","invitations","ai","cost","members"].map((v) => (
            <TabsTrigger key={v} value={v} className="!rounded-none border-b-2 border-transparent data-[state=active]:border-white data-[state=active]:bg-transparent data-[state=active]:text-foreground uppercase tracking-widest text-[10px] mono px-3" data-testid={`settings-tab-${v}`}>
              {v}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="broker"><BrokerPanel/></TabsContent>
        <TabsContent value="risk"><RiskPanel/></TabsContent>
        <TabsContent value="mfa"><MFAPanel refresh={refreshMe} user={user}/></TabsContent>
        <TabsContent value="invitations"><InvitesPanel user={user}/></TabsContent>
        <TabsContent value="ai"><AIPanel user={user}/></TabsContent>
        <TabsContent value="cost"><CostCapsPanel user={user}/></TabsContent>
        <TabsContent value="members"><MembersPanel/></TabsContent>
      </Tabs>
    </div>
  );
}

function BrokerPanel() {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("mock");
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [rotateKey, setRotateKey] = useState("");
  const [rotateSecret, setRotateSecret] = useState("");
  const [health, setHealth] = useState(null);
  const [recon, setRecon] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [rebalancing, setRebalancing] = useState(false);
  const [rebalancePreview, setRebalancePreview] = useState(null);
  const [rebalanceConfirming, setRebalanceConfirming] = useState(false);
  const [rebalanceForce, setRebalanceForce] = useState(false);
  const [rebalanceReason, setRebalanceReason] = useState("");
  const [rebalanceTyped, setRebalanceTyped] = useState("");
  // Intent Resolution modal — nested sheet inside the rebalance preview.
  const [resolveIntent, setResolveIntent] = useState(null); // {id, symbol, side, order_type, requested_qty, execution_source, broker_order_id}
  const [resolveChoice, setResolveChoice] = useState("abandoned");
  const [resolveReason, setResolveReason] = useState("");
  const [resolveSubmitting, setResolveSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [showRotateForm, setShowRotateForm] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    const { data } = await api.get("/broker/status");
    setStatus(data);
  };
  const loadHealth = async () => {
    if (!status || status.mode === "disconnected") { setHealth(null); return; }
    try {
      const { data } = await api.get("/broker/health");
      setHealth(data);
    } catch { /* ignore transient health failures */ }
  };
  const loadRecon = async () => {
    if (!status || status.mode === "disconnected") { setRecon(null); return; }
    try {
      const { data } = await api.get("/broker/reconciliation");
      setRecon(data && !data.empty ? data : null);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Deep-link support: /settings#broker scrolls to the broker panel.
    if (typeof window === "undefined") return;
    if (window.location.hash === "#broker") {
      setTimeout(() => {
        document.getElementById("broker")?.scrollIntoView({
          behavior: "smooth", block: "start" });
      }, 200);
    }
  }, []);
  useEffect(() => { loadHealth(); loadRecon(); }, [status?.mode, status?.id]);

  // ---- Persistent-connection UX -----------------------------------------
  // Derived flags: `credentialsValid` = we have stored creds AND Alpaca
  // isn't rejecting them.  When true, the connect form stays collapsed.
  // A 401/403 flip (`reconciliation_credentials_state === "INVALID"`)
  // auto-expands the connect form so the operator sees the prompt.
  const isConnected = status && status.mode !== "disconnected";
  const credentialsValid = isConnected
    && status.reconciliation_credentials_state !== "INVALID"
    && status.connection_state !== "AUTH_FAILED";
  useEffect(() => {
    if (!status) return;
    if (!credentialsValid) {
      setShowConnectForm(true);       // needs credentials → prompt
    } else {
      setShowConnectForm(false);      // stored creds valid → hide form
      setShowRotateForm(false);
    }
  }, [credentialsValid, status?.mode]);

  const reconnect = async () => {
    setReconnecting(true);
    try {
      const { data } = await api.post("/broker/test");
      setHealth(data);
      toast[data.ok ? "success" : "error"](
        data.ok ? "Broker reconnected — stored keys verified"
                    : (data.errors?.[0] || "Reconnect failed"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconnect failed");
    } finally { setReconnecting(false); }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const payload = { mode };
      if (mode === "alpaca_paper") { payload.api_key = key.trim(); payload.api_secret = secret.trim(); }
      const { data } = await api.post("/broker/connect", payload);
      toast.success(`Connected: ${mode.replace("_"," ")}`);
      setKey(""); setSecret("");
      setShowConnectForm(false);
      // Fresh connection → let the source auto-follow the new broker.
      resetExecutionSourceOverride();
      notifyBrokerChanged(data?.mode || mode);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Connect failed"); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/broker/test");
      setHealth(data);
      toast[data.ok ? "success" : "error"](
        data.ok ? `Connection ${data.state}` : (data.errors?.[0] || "Connection test failed")
      );
      await load();
    } finally { setTesting(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/broker/sync");
      setRecon(data);
      const mismatches = (data.deltas || []).filter(d => d.severity === "mismatch").length;
      toast[mismatches === 0 ? "success" : "error"](
        mismatches === 0 ? "Reconciliation OK" : `${mismatches} mismatch${mismatches === 1 ? "" : "es"} found`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const rotate = async () => {
    if (!rotateKey || !rotateSecret) return;
    setRotating(true);
    try {
      await api.post("/broker/rotate-keys", { mode: "alpaca_paper",
        api_key: rotateKey.trim(), api_secret: rotateSecret.trim() });
      toast.success("Credentials rotated");
      setRotateKey(""); setRotateSecret("");
      setShowRotateForm(false);
      notifyBrokerChanged("alpaca_paper");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rotation failed"); }
    finally { setRotating(false); }
  };

  const disconnect = async () => {
    const ok = await confirm.confirm({
      variant: "destructive",
      title: "Disconnect broker?",
      description: "Your API keys will be wiped and you will need to reconnect to submit new orders. Existing positions remain in the workspace ledger.",
      confirmLabel: "Disconnect",
      testId: "broker-disconnect-confirm",
    });
    if (!ok) return;
    await api.delete("/broker");
    resetExecutionSourceOverride();
    notifyBrokerChanged("disconnected");
    load();
  };

  const rebalanceBuckets = async () => {
    setRebalancing(true);
    setRebalanceForce(false); setRebalanceReason(""); setRebalanceTyped("");
    try {
      const { data } = await api.post("/broker/portfolios/rebalance/preview");
      setRebalancePreview(data);
      // The preview endpoint clears a stale needs_rebalance_review flag when
      // it detects the portfolio is already balanced. Notify listeners so the
      // BrokerCockpit refetches and the amber banner disappears immediately.
      notifyBrokerChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load rebalance preview");
    } finally { setRebalancing(false); }
  };

  // Allow the BrokerCockpit's "Portfolio needs rebalance" hint to open the
  // preview modal. The modal itself now lives at App root (see
  // ``RebalanceBucketsFlow``) so it renders no matter which page the user
  // is on. This local listener is intentionally left as a no-op fallback:
  // the global component owns the entire flow.
  // (Removed: `rebalanceBuckets()` listener — now global.)

  const confirmRebalance = async () => {
    if (!rebalancePreview) return;
    const br = rebalancePreview.broker_snapshot || {};
    setRebalanceConfirming(true);
    try {
      const body = {
        idempotency_key: `${rebalancePreview.preview_token}-${Date.now()}`,
        expected_equity: br.equity,
        expected_cash: br.cash,
        expected_position_value: br.invested_value,
        expected_execution_source: rebalancePreview.execution_source?.after,
        force: rebalanceForce,
      };
      if (rebalanceForce) {
        body.reason = rebalanceReason.trim();
        body.typed_confirmation = rebalanceTyped.trim();
      }
      const { data } = await api.post("/broker/portfolios/rebalance/confirm", body);
      if (data.no_op) {
        toast.success("No rebalance needed — buckets already match this broker account");
      } else if (data.idempotent_replay) {
        toast.success("Rebalance already applied (idempotent replay)");
      } else {
        toast.success(`Reset ${data.buckets_reset} bucket${data.buckets_reset === 1 ? "" : "s"} · Baseline: ${data.snapshot_id?.slice(0, 12)}…`);
      }
      setRebalancePreview(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.startsWith("REBALANCE_STALE_PREVIEW")) {
        toast.error("Broker state changed since preview — refreshing…");
        rebalanceBuckets();
      } else if (detail.startsWith("REBALANCE_HARD_BLOCK")) {
        toast.error("Hard block — cannot be overridden. Resolve at the broker first.");
      } else if (detail.startsWith("REBALANCE_FORCE_CONFIRMATION")) {
        toast.error("Type FORCE SWITCH exactly to confirm.");
      } else if (detail.startsWith("REBALANCE_FORCE_REASON")) {
        toast.error("Provide a reason of at least 10 characters.");
      } else if (detail.startsWith("REBALANCE_FORCE_ROLE")) {
        toast.error("Only workspace owners can force override.");
      } else if (detail.startsWith("REBALANCE_BLOCKED")) {
        toast.error("Blocked by open records — enable Force and provide a reason.");
      } else {
        toast.error(detail || "Rebalance failed");
      }
    } finally { setRebalanceConfirming(false); }
  };

  const cancelIntentFromModal = async (intent) => {
    // Open the resolve modal — the raw cancel endpoint isn't safe for MOCK
    // intents (it queries Alpaca). The resolve endpoint handles both mock
    // and Alpaca cleanly.
    setResolveIntent(intent);
    setResolveChoice("abandoned");
    setResolveReason("");
  };

  const submitResolveIntent = async () => {
    if (!resolveIntent) return;
    const trimmed = resolveReason.trim();
    if (trimmed.length < 10) {
      toast.error("Reason must be at least 10 characters");
      return;
    }
    setResolveSubmitting(true);
    try {
      await api.post(`/broker/orders/intents/${resolveIntent.id}/resolve`, {
        resolution: resolveChoice,
        reason: trimmed,
      });
      toast.success(`Intent ${resolveIntent.id.slice(-6)} resolved — refreshing preview`);
      setResolveIntent(null);
      setResolveReason("");
      // Refresh the preview so the blocking list drops the row.
      rebalanceBuckets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resolve failed");
    } finally {
      setResolveSubmitting(false);
    }
  };

  const stateTone = (s) => {
    if (s === "HEALTHY") return "pnl-up border-current";
    if (s === "CONNECTED_WITH_WARNING") return "text-signal-yellow border-current";
    if (s === "AUTH_FAILED" || s === "DISCONNECTED" || s === "SYNC_FAILED") return "pnl-down border-current";
    if (s === "RESTRICTED" || s === "DEGRADED" || s === "RECONCILIATION_REQUIRED") return "text-signal-yellow border-current";
    return "text-muted-foreground";
  };
  const mismatches = (recon?.deltas || []).filter(d => d.severity === "mismatch");

  return (
    <div className="mt-6 space-y-3" data-testid="broker-panel" id="broker">
      {/* Professional cockpit view — 5-second-glance broker status */}
      <BrokerCockpit brokerAccount={status} onBrokerSwitch={load}/>

      {/* Portfolio rebalance (Sync Buckets to Broker) + Intent resolution
          modals live at page-level so they can be triggered from anywhere in
          the cockpit. All duplicate broker-status/health/rotate/reconciliation
          UI has been removed — the cockpit above owns those flows. */}
      {/* Rebalance preview modal — Stage B4 broker-switch safety */}
      {rebalancePreview && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="rebalance-preview-modal" onClick={(e) => { if (e.target === e.currentTarget) setRebalancePreview(null); }}>
          <div className="bg-background border border-border/80 max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-4 border-b border-border/60 flex items-center justify-between">
              <div>
                <div className="label-cap">Sync Buckets to Broker</div>
                <div className="text-sm font-medium mt-1">Review the changes before confirming</div>
              </div>
              <button className="btn-outline" onClick={() => setRebalancePreview(null)} data-testid="rebalance-preview-close">Cancel</button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              {/* Broker state */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-border/60 p-3">
                  <div className="label-cap mb-2">Broker Snapshot</div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Mode</span><span className="mono">{rebalancePreview.broker_snapshot?.mode}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Equity</span><span className="mono">${(rebalancePreview.broker_snapshot?.equity ?? 0).toLocaleString(undefined,{minimumFractionDigits:2})}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Cash</span><span className="mono">${(rebalancePreview.broker_snapshot?.cash ?? 0).toLocaleString(undefined,{minimumFractionDigits:2})}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Invested</span><span className="mono">${(rebalancePreview.broker_snapshot?.invested_value ?? 0).toLocaleString(undefined,{minimumFractionDigits:2})}</span></div>
                  <div className="flex justify-between font-medium"><span>Deployable</span><span className="mono pnl-up">${(rebalancePreview.broker_snapshot?.deployable_cash ?? 0).toLocaleString(undefined,{minimumFractionDigits:2})}</span></div>
                </div>
                <div className="border border-border/60 p-3">
                  <div className="label-cap mb-2">Execution Source</div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Before</span><span className="chip text-signal-yellow border-current">{rebalancePreview.execution_source?.before}</span></div>
                  <div className="flex justify-between mt-2"><span className="text-muted-foreground">After</span><span className="chip pnl-up border-current">{rebalancePreview.execution_source?.after}</span></div>
                  <div className="flex justify-between mt-2 text-xs"><span className="text-muted-foreground">Fingerprint</span><span className={`mono ${rebalancePreview.fingerprint?.changed ? "pnl-up" : "text-muted-foreground"}`}>{rebalancePreview.fingerprint?.changed ? "CHANGED" : "UNCHANGED"}</span></div>
                  <div className="mt-2 text-[11px] text-muted-foreground">Mock realized P&L and closed trades will be archived to <span className="mono">portfolio_baselines</span> and remain accessible.</div>
                </div>
              </div>
              {/* Per-bucket diff */}
              <div className="border border-border/60">
                <div className="grid grid-cols-5 gap-2 p-2 text-[11px] label-cap border-b border-border/60">
                  <div>Bucket</div>
                  <div className="text-right">Before Cash</div>
                  <div className="text-right">After Cash</div>
                  <div className="text-right">Before P&L</div>
                  <div className="text-right">After P&L</div>
                </div>
                {(rebalancePreview.buckets || []).map((b) => (
                  <div key={b.id} className="grid grid-cols-5 gap-2 p-2 text-sm border-b border-border/40 last:border-b-0" data-testid={`rebalance-row-${b.key}`}>
                    <div>{b.name} <span className="text-[10px] text-muted-foreground">({b.allocation_pct}%)</span></div>
                    <div className="text-right mono">${(b.before?.available_cash ?? 0).toLocaleString(undefined,{minimumFractionDigits:2})}</div>
                    <div className="text-right mono pnl-up">${(b.after?.available_cash ?? 0).toLocaleString(undefined,{minimumFractionDigits:2})}</div>
                    <div className="text-right mono">${(b.before?.realized_pnl ?? 0).toLocaleString(undefined,{minimumFractionDigits:2})}</div>
                    <div className="text-right mono text-muted-foreground">$0.00</div>
                  </div>
                ))}
              </div>
              {/* Warnings */}
              {(rebalancePreview.hard_blocks || []).length > 0 && (
                <div className="border pnl-down border-current p-3">
                  <div className="label-cap mb-1">HARD BLOCKS · CANNOT BE OVERRIDDEN</div>
                  {rebalancePreview.hard_blocks.map((b, i) => (
                    <div key={i} className="text-[11px] mt-1" data-testid={`rebalance-hardblock-${b.code}`}>
                      <span className="mono mr-2">{b.code}</span>{b.detail}
                    </div>
                  ))}
                </div>
              )}
              {(rebalancePreview.warnings || []).length > 0 && (
                <div className="space-y-1">
                  {rebalancePreview.warnings.map((w, i) => (
                    <div key={i} className={`text-[11px] p-2 border ${w.severity === "HIGH" ? "pnl-down border-current" : w.severity === "MEDIUM" ? "text-signal-yellow border-current" : "text-muted-foreground border-border/60"}`} data-testid={`rebalance-warning-${w.code}`}>
                      <span className="label-cap mr-2">{w.severity}</span>{w.detail}
                    </div>
                  ))}
                </div>
              )}
              {/* Blocking records — pending intents visible per-row */}
              {rebalancePreview.blocking_records?.open_order_intents?.length > 0 && (
                <div className="border border-border/60">
                  <div className="p-2 label-cap border-b border-border/60">Pending Order Intents</div>
                  {rebalancePreview.blocking_records.open_order_intents.map((oi) => (
                    <div key={oi.id} className="grid grid-cols-6 gap-2 p-2 text-[11px] border-b border-border/40 last:border-b-0" data-testid={`rebalance-intent-${oi.id.slice(-8)}`}>
                      <div className="mono col-span-1">{oi.symbol} <span className="text-muted-foreground">{oi.side}</span></div>
                      <div className="mono">{oi.order_type} · {oi.requested_qty}</div>
                      <div><span className="chip text-signal-yellow border-current">{oi.status}</span></div>
                      <div className="text-muted-foreground truncate">{oi.broker_order_id || "no broker id"}</div>
                      <div className="text-muted-foreground text-right">{oi.execution_source}</div>
                      <div className="text-right"><button className="btn-outline !text-[10px] !px-2 !py-0.5" onClick={() => cancelIntentFromModal(oi)} data-testid={`rebalance-intent-resolve-${oi.id.slice(-8)}`}>Resolve</button></div>
                    </div>
                  ))}
                </div>
              )}
              {/* Unmapped broker positions */}
              {rebalancePreview.blocking_records?.open_broker_positions?.length > 0 && (
                <div className="border border-border/60">
                  <div className="p-2 label-cap border-b border-border/60">Broker Positions</div>
                  {rebalancePreview.blocking_records.open_broker_positions.map((bp) => (
                    <div key={bp.id} className="grid grid-cols-5 gap-2 p-2 text-[11px] border-b border-border/40 last:border-b-0">
                      <div className="mono">{bp.symbol}</div>
                      <div className="mono text-right">{bp.confirmed_qty} @ ${(bp.avg_entry_price ?? 0).toFixed(2)}</div>
                      <div className="mono text-right">{bp.execution_source || "—"}</div>
                      <div className="text-right"><span className={`chip ${bp.ownership === "MAPPED" ? "pnl-up" : "text-signal-yellow"} border-current`}>{bp.ownership}</span></div>
                      <div className="text-muted-foreground text-right text-[10px]">{bp.recommended_action}</div>
                    </div>
                  ))}
                </div>
              )}
              {/* Force override — hardened UX */}
              {rebalancePreview.blocks_switch && !rebalancePreview.hard_blocks?.length && rebalancePreview.force_allowed && (
                <div className="border pnl-down border-current p-3 space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={rebalanceForce} onChange={(e) => setRebalanceForce(e.target.checked)} data-testid="rebalance-force-checkbox"/>
                    <span className="font-medium">Force override — resolve pending records manually later</span>
                  </label>
                  {rebalanceForce && (
                    <div className="space-y-2 pt-1 border-t border-current/30">
                      <div>
                        <div className="label-cap mb-1">Reason (min 10 chars, stored on audit)</div>
                        <textarea className="input w-full text-xs" rows={2} value={rebalanceReason} onChange={(e) => setRebalanceReason(e.target.value)} placeholder="e.g. Two abandoned mock test intents from a prior session — will not affect Alpaca" data-testid="rebalance-force-reason"/>
                      </div>
                      <div>
                        <div className="label-cap mb-1">Type <span className="mono">FORCE SWITCH</span> to confirm</div>
                        <input className="input w-full text-xs mono" value={rebalanceTyped} onChange={(e) => setRebalanceTyped(e.target.value)} placeholder="FORCE SWITCH" data-testid="rebalance-force-typed"/>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-border/60 flex justify-end gap-2">
              <button className="btn-outline" onClick={() => setRebalancePreview(null)} data-testid="rebalance-cancel-btn">Cancel</button>
              <button className="btn-danger" onClick={confirmRebalance} disabled={rebalanceConfirming || (rebalancePreview.hard_blocks?.length > 0) || (rebalancePreview.blocks_switch && !rebalanceForce) || (rebalanceForce && (rebalanceReason.trim().length < 10 || rebalanceTyped.trim().toUpperCase() !== "FORCE SWITCH"))} data-testid="rebalance-confirm-btn">
                {rebalanceConfirming ? "Applying…" : rebalancePreview.hard_blocks?.length > 0 ? "Blocked" : rebalanceForce ? "Force and Switch" : "Confirm and Switch"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Intent Resolution modal — nested inside the rebalance preview.
          Handles both MOCK and Alpaca intents safely via /resolve endpoint. */}
      {resolveIntent && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="resolve-intent-modal" onClick={(e) => { if (e.target === e.currentTarget && !resolveSubmitting) setResolveIntent(null); }}>
          <div className="bg-background border border-border/80 max-w-lg w-full">
            <div className="p-4 border-b border-border/60">
              <div className="label-cap">Resolve Order Intent</div>
              <div className="text-sm font-medium mt-1">
                {resolveIntent.symbol} · {resolveIntent.side} · {resolveIntent.order_type} · qty {resolveIntent.requested_qty}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 mono">
                {resolveIntent.broker_order_id || "no broker id"} · {resolveIntent.execution_source}
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <div className="label-cap mb-2">Resolution</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    { v: "cancelled", label: "Cancelled", hint: "Intent was cancelled at broker or never sent" },
                    { v: "abandoned", label: "Abandoned", hint: "Legacy / no longer relevant — will not be re-submitted" },
                    { v: "duplicate_legacy", label: "Duplicate Legacy", hint: "Duplicates a MOCK-era intent that never reached Alpaca" },
                    { v: "manual_review", label: "Manual Review", hint: "Escalate for investigation — do not close" },
                  ].map(opt => (
                    <label key={opt.v} className={`border p-2 cursor-pointer ${resolveChoice === opt.v ? "border-current pnl-up" : "border-border/60"}`}>
                      <div className="flex items-center gap-2">
                        <input type="radio" name="resolve-choice" checked={resolveChoice === opt.v} onChange={() => setResolveChoice(opt.v)} data-testid={`resolve-choice-${opt.v}`}/>
                        <span className="font-medium">{opt.label}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">{opt.hint}</div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-cap mb-1">Reason (min 10 chars — stored on audit)</div>
                <textarea
                  className="input w-full text-xs"
                  rows={3}
                  value={resolveReason}
                  onChange={(e) => setResolveReason(e.target.value)}
                  placeholder="e.g. Legacy MOCK stop_limit from a prior fork — never reached Alpaca"
                  data-testid="resolve-reason-input"
                />
                <div className="text-[10px] text-muted-foreground mt-1">{resolveReason.trim().length}/10 minimum</div>
              </div>
            </div>
            <div className="p-4 border-t border-border/60 flex justify-end gap-2">
              <button className="btn-outline" onClick={() => setResolveIntent(null)} disabled={resolveSubmitting} data-testid="resolve-cancel-btn">Back</button>
              <button className="btn-danger" onClick={submitResolveIntent} disabled={resolveSubmitting || resolveReason.trim().length < 10} data-testid="resolve-submit-btn">
                {resolveSubmitting ? "Resolving…" : "Resolve Intent"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RiskPanel() {
  const [initial, setInitial] = useState(null);
  const confirm = useConfirm();
  useEffect(() => { api.get("/risk/profile").then((r) => setInitial(r.data)); }, []);

  const { dirty, values, setValue, save, discard, savedAt, saving, errors } = useUnsavedChanges({
    initial: initial || {},
    onSave: async (v) => {
      // Owner-sensitive change: password confirm before writing
      const pw = await confirm.promptPassword({
        variant: "risk",
        title: "Confirm password to change risk limits",
        description: "Risk-profile changes are sensitive because they widen the boundaries the Risk Engine enforces on every trade. Please re-verify your password to continue.",
        placeholder: "Password",
        confirmLabel: "Save Risk Profile",
        testId: "risk-save-password-prompt",
      });
      if (!pw) throw new Error("Password required");
      // NOTE: backend accepts changes without pw for MVP; we prompt as UX guard.
      await api.put("/risk/profile", v);
      toast.success("Risk profile updated");
      return v;
    },
  });

  if (!initial) return <div className="mt-6 text-sm text-muted-foreground">Loading…</div>;

  const fields = [
    { k: "max_position_pct", label: "Max position size (% of equity)" },
    { k: "max_daily_loss_pct", label: "Max daily loss (%)" },
    { k: "max_weekly_loss_pct", label: "Max weekly loss (%)" },
    { k: "max_open_positions", label: "Max open positions" },
    { k: "max_sector_pct", label: "Max sector exposure (%)" },
    { k: "default_stop_pct", label: "Default stop (% below entry)" },
    { k: "default_target_pct", label: "Default target (% above entry)" },
  ];

  return (
    <>
      <UnsavedChangesGuard dirty={dirty} onSave={save} />
      <form data-testid={SETTINGS.riskProfileForm} onSubmit={(e)=>{e.preventDefault();save();}} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 pb-24">
        {fields.map((f) => (
          <div key={f.k}>
            <div className="label-cap mb-1">{f.label}</div>
            <Input
              type="number" step="any"
              value={values[f.k] ?? ""}
              onChange={(e)=>setValue(f.k, parseFloat(e.target.value))}
              className="!rounded-none mono"
              data-testid={`risk-input-${f.k}`}
            />
          </div>
        ))}
      </form>
      <SaveBar dirty={dirty} saving={saving} savedAt={savedAt} errors={errors} onSave={save} onDiscard={discard} testId="save-bar-risk" />
    </>
  );
}

function MFAPanel({ refresh, user }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [pw, setPw] = useState("");

  const beginSetup = async () => {
    const { data } = await api.post("/auth/mfa/setup");
    setSetup(data);
  };
  const verify = async () => {
    try {
      const { data } = await api.post("/auth/mfa/verify", { code });
      setCodes(data.recovery_codes);
      toast.success("MFA enabled.");
      refresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
  };
  const disable = async () => {
    try {
      await api.post("/auth/mfa/disable", { password: pw });
      toast.success("MFA disabled");
      refresh?.();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-border/60">
      <div className="grid-cell">
        <div className="label-cap mb-3">Two-Factor Authentication</div>
        <div className="text-sm mb-3">Status: <span className={user?.mfa_enabled ? "pnl-up mono" : "text-signal-yellow mono"}>{user?.mfa_enabled ? "ENABLED" : "DISABLED"}</span></div>
        {!user?.mfa_enabled && (
          <>
            {!setup && <button className="btn-primary" onClick={beginSetup} data-testid={SETTINGS.mfaSetupBtn}>Begin MFA Setup</button>}
            {setup && (
              <div className="space-y-3">
                <div className="text-xs">Scan this in your authenticator app (Google Authenticator, 1Password, Authy):</div>
                <div className="mono text-xs break-all border border-border/60 p-2">{setup.otpauth_uri}</div>
                <div className="text-xs">Or enter this secret manually: <span className="mono">{setup.secret}</span></div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1"><div className="label-cap mb-1">Enter 6-digit code</div><Input value={code} onChange={(e)=>setCode(e.target.value)} inputMode="numeric" className="!rounded-none mono" data-testid={SETTINGS.mfaVerifyCode}/></div>
                  <button className="btn-primary" onClick={verify} data-testid={SETTINGS.mfaVerifyBtn}>Verify & Enable</button>
                </div>
                {codes && (
                  <div className="border border-border/60 p-3 space-y-1 text-xs">
                    <div className="label-cap">Recovery Codes — store these securely</div>
                    <div className="grid grid-cols-2 gap-1 mono">{codes.map((c)=><div key={c}>{c}</div>)}</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {user?.mfa_enabled && (
          <div className="space-y-3">
            <Input type="password" placeholder="Your password" value={pw} onChange={(e)=>setPw(e.target.value)} className="!rounded-none"/>
            <button className="btn-danger" onClick={disable}>Disable MFA</button>
          </div>
        )}
      </div>
      <div className="grid-cell text-sm text-muted-foreground">
        <div className="label-cap mb-2">Why MFA?</div>
        <p>Optional in the paper-trading MVP, mandatory before live trading is ever introduced. Sensitive actions (broker replace, risk limit changes, kill-switch reactivation) require password (or MFA) re-verification.</p>
      </div>
    </div>
  );
}

function InvitesPanel({ user }) {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("trader");
  const isOwner = user?.role === "owner";
  const load = async () => { if (!isOwner) return; const { data } = await api.get("/auth/invites"); setInvites(data); };
  useEffect(() => { load(); }, [isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async () => {
    try {
      const { data } = await api.post("/auth/invites", { email, role });
      toast.success(`Invite created. Share: ${window.location.origin}${data.invite_url_hint}`);
      setEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!isOwner) return <div className="mt-6 text-sm text-muted-foreground">Owner-only.</div>;

  return (
    <div className="mt-6 space-y-3">
      <div className="flex gap-2 items-end">
        <div className="flex-1"><div className="label-cap mb-1">Email</div><Input value={email} onChange={(e)=>setEmail(e.target.value)} className="!rounded-none" data-testid={SETTINGS.inviteEmail}/></div>
        <div><div className="label-cap mb-1">Role</div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="!rounded-none h-9 bg-transparent border-border/60 text-sm min-w-[120px]" data-testid="invite-role-select">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent className="!rounded-none">
              <SelectItem value="trader">trader</SelectItem>
              <SelectItem value="owner">owner</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <button className="btn-primary" onClick={invite} data-testid={SETTINGS.inviteBtn}>Create Invite</button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20">
          <tr><th className="text-left px-3 py-1.5">Email</th><th className="text-left px-3 py-1.5">Role</th><th className="text-left px-3 py-1.5">Status</th><th className="text-left px-3 py-1.5">Link</th></tr>
        </thead>
        <tbody>
          {invites.map((i) => (
            <tr key={i.id} className="border-t border-border/40">
              <td className="px-3 py-2">{i.email}</td>
              <td className="px-3 py-2 mono uppercase">{i.role}</td>
              <td className="px-3 py-2">{i.accepted ? <span className="pnl-up">Accepted</span> : <span className="text-signal-yellow">Pending</span>}</td>
              <td className="px-3 py-2 mono text-xs break-all">{i.accepted ? "—" : `${window.location.origin}/register?token=${i.token}`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AIPanel({ user }) {
  const [initial, setInitial] = useState([]);
  const [usage, setUsage] = useState([]);
  const isOwner = user?.role === "owner";
  const load = async () => {
    const [a, u] = await Promise.all([api.get("/workspace/ai-agents"), api.get("/workspace/ai-usage")]);
    setInitial(a.data); setUsage(u.data);
  };
  useEffect(() => { load(); }, []);

  const { dirty, values: agents, setValues, save, discard, savedAt, saving, errors } = useUnsavedChanges({
    initial: initial,
    onSave: async (list) => {
      if (!isOwner) throw new Error("Owner-only");
      // PUT only agents whose fields changed since baseline
      const originalByAgent = Object.fromEntries((initial || []).map(a => [a.agent, a]));
      const changed = (list || []).filter(a => JSON.stringify(a) !== JSON.stringify(originalByAgent[a.agent]));
      for (const a of changed) {
        await api.put(`/workspace/ai-agents/${a.agent}`, {
          provider: a.provider, model: a.model, temperature: a.temperature,
          max_tokens: a.max_tokens, prompt_version: a.prompt_version,
          enabled: a.enabled, timeout_s: a.timeout_s,
        });
      }
      toast.success(`Updated ${changed.length} agent${changed.length === 1 ? "" : "s"}`);
      return list;
    },
  });

  const updateAgent = (agent, patch) => {
    setValues((prev) => (prev || []).map(a => a.agent === agent ? { ...a, ...patch } : a));
  };

  // When Provider changes, snap Model to that provider's default IF the current
  // Model doesn't belong to the new provider's catalog (avoids silent Claude→Gemini
  // string mismatches that break the call).
  const changeProvider = (agent, provider) => {
    setValues((prev) => (prev || []).map(a => {
      if (a.agent !== agent) return a;
      const catalog = PROVIDER_MODELS[provider] || [];
      const modelStillValid = catalog.some(m => m.id === a.model);
      return {
        ...a, provider,
        model: modelStillValid ? a.model : (PROVIDER_DEFAULT_MODEL[provider] || catalog[0]?.id || ""),
      };
    }));
  };

  return (
    <>
      {isOwner && <UnsavedChangesGuard dirty={dirty} onSave={save} />}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-border/60 pb-24">
        <div className="grid-cell">
          <div className="label-cap mb-3">AI Model Registry</div>
          <div className="space-y-4">
            {(agents || []).map((a) => (
              <div key={a.agent} className="border border-border/60 p-3" data-testid={`ai-agent-${a.agent}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="mono uppercase text-sm">{a.agent.replace("_"," ")}</div>
                  <div className="ml-auto text-[10px] mono text-muted-foreground uppercase tracking-widest">{a.enabled ? "Enabled" : "Disabled"}</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="label-cap mb-1">Provider</div>
                    <Select value={a.provider} onValueChange={(v) => changeProvider(a.agent, v)} disabled={!isOwner}>
                      <SelectTrigger className="!rounded-none h-8 bg-transparent border-border/60 text-xs" data-testid={`ai-provider-${a.agent}`}>
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent className="!rounded-none">
                        <SelectItem value="openai"    data-testid={`ai-provider-${a.agent}-openai`}>OpenAI</SelectItem>
                        <SelectItem value="anthropic" data-testid={`ai-provider-${a.agent}-anthropic`}>Anthropic</SelectItem>
                        <SelectItem value="gemini"    data-testid={`ai-provider-${a.agent}-gemini`}>Gemini</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="label-cap mb-1">Model</div>
                    <Select value={a.model} onValueChange={(v) => updateAgent(a.agent, { model: v })} disabled={!isOwner}>
                      <SelectTrigger className="!rounded-none h-8 bg-transparent border-border/60 mono text-[11px]" data-testid={`ai-model-${a.agent}`}>
                        <SelectValue placeholder="Select model"/>
                      </SelectTrigger>
                      <SelectContent className="!rounded-none">
                        {(PROVIDER_MODELS[a.provider] || []).map((m) => (
                          <SelectItem key={m.id} value={m.id} className="mono text-[11px]"
                            data-testid={`ai-model-${a.agent}-${m.id}`}>
                            {m.label}
                          </SelectItem>
                        ))}
                        {/* Preserve any custom / legacy model that isn't in the catalog */}
                        {a.model && !(PROVIDER_MODELS[a.provider] || []).some((m) => m.id === a.model) && (
                          <SelectItem value={a.model} className="mono text-[11px] italic">
                            {a.model} (custom)
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="label-cap mb-1">Temperature</div>
                    <Input disabled={!isOwner} type="number" step="0.1" className="!rounded-none mono h-8" value={a.temperature} onChange={(e)=>updateAgent(a.agent,{temperature:parseFloat(e.target.value)})}/>
                  </div>
                  <div>
                    <div className="label-cap mb-1">Max tokens</div>
                    <Input disabled={!isOwner} type="number" className="!rounded-none mono h-8" value={a.max_tokens} onChange={(e)=>updateAgent(a.agent,{max_tokens:parseInt(e.target.value)||2000})}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid-cell">
          <div className="label-cap mb-3">AI Usage</div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr><th className="text-left py-1">User</th><th className="text-right py-1">Calls</th><th className="text-right py-1">In tok</th><th className="text-right py-1">Out tok</th><th className="text-right py-1">Cost $</th></tr>
            </thead>
            <tbody>
              {usage.map((u) => (
                <tr key={u.user_id} className="border-t border-border/40">
                  <td className="py-1.5">{u.email}</td>
                  <td className="py-1.5 text-right mono">{u.calls}</td>
                  <td className="py-1.5 text-right mono">{u.tokens_in}</td>
                  <td className="py-1.5 text-right mono">{u.tokens_out}</td>
                  <td className="py-1.5 text-right mono">{u.cost}</td>
                </tr>
              ))}
              {usage.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No AI calls yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {isOwner && <SaveBar dirty={dirty} saving={saving} savedAt={savedAt} errors={errors} onSave={save} onDiscard={discard} testId="save-bar-ai" />}
    </>
  );
}

function MembersPanel() {
  const [members, setMembers] = useState([]);
  useEffect(() => { api.get("/workspace/members").then((r)=>setMembers(r.data)); }, []);
  return (
    <div className="mt-6">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
          <tr><th className="text-left py-1.5 px-3">Name</th><th className="text-left py-1.5 px-3">Email</th><th className="text-left py-1.5 px-3">Role</th><th className="text-left py-1.5 px-3">MFA</th></tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} className="border-t border-border/40">
              <td className="px-3 py-2">{m.name}</td>
              <td className="px-3 py-2">{m.email}</td>
              <td className="px-3 py-2 mono uppercase">{m.role}</td>
              <td className="px-3 py-2">{m.mfa_enabled ? <span className="pnl-up mono">ON</span> : <span className="text-muted-foreground mono">OFF</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostCapsPanel({ user }) {
  const isOwner = user?.role === "owner";
  const [initial, setInitial] = useState(null);
  const [summary, setSummary] = useState(null);
  const load = async () => {
    const [c, s] = await Promise.all([api.get("/workspace/ai-cost-caps"), api.get("/workspace/ai-cost-usage-summary")]);
    setInitial(c.data); setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  const { dirty, values: cfg, setValue, setValues, save, discard, savedAt, saving, errors } = useUnsavedChanges({
    initial: initial || {},
    onSave: async (v) => {
      if (!isOwner) throw new Error("Owner-only");
      await api.put("/workspace/ai-cost-caps", v);
      toast.success("Cost caps updated");
      // Reload live usage bars
      const { data: s } = await api.get("/workspace/ai-cost-usage-summary");
      setSummary(s);
      return v;
    },
  });
  if (!initial || !summary) return <div className="mt-6 text-sm text-muted-foreground">Loading…</div>;

  const Bar = ({ used, cap, label, testid }) => {
    const pct = Math.min(100, (used / (cap || 1)) * 100);
    const tone = pct >= 100 ? "bg-signal-red" : pct >= 90 ? "bg-signal-red/70" : pct >= 75 ? "bg-signal-yellow" : "bg-[#0A84FF]";
    return (
      <div>
        <div className="flex justify-between text-[10px] mono uppercase tracking-widest text-muted-foreground">
          <span>{label}</span>
          <span data-testid={testid}>${used.toFixed(4)} / ${cap}</span>
        </div>
        <div className="h-1.5 bg-secondary/50 mt-1"><div className={`h-full ${tone}`} style={{ width: `${pct}%` }}/></div>
      </div>
    );
  };

  const fields = [
    ["daily_per_user_usd", "Daily per user ($)"],
    ["monthly_per_user_usd", "Monthly per user ($)"],
    ["daily_workspace_usd", "Daily workspace ($)"],
    ["monthly_workspace_usd", "Monthly workspace ($)"],
  ];

  return (
    <>
      {isOwner && <UnsavedChangesGuard dirty={dirty} onSave={save} />}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-border/60 pb-24">
        <div className="grid-cell">
          <div className="label-cap mb-3">AI Cost Limits</div>
          <div className="grid grid-cols-2 gap-3">
            {fields.map(([k,l]) => (
              <div key={k}>
                <div className="label-cap mb-1">{l}</div>
                <Input type="number" step="0.5" value={cfg[k] ?? ""} disabled={!isOwner}
                  onChange={(e)=>setValue(k, parseFloat(e.target.value)||0)}
                  className="!rounded-none mono"
                  data-testid={`cost-input-${k}`}/>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <label className="flex items-center gap-2 border border-border/60 p-2 cursor-pointer">
              <input type="checkbox" disabled={!isOwner} checked={cfg.hard_block ?? false} onChange={(e)=>setValue("hard_block", e.target.checked)}/>
              Hard block at 100%
            </label>
            <label className="flex items-center gap-2 border border-border/60 p-2 cursor-pointer">
              <input type="checkbox" disabled={!isOwner} checked={cfg.safety_features_exempt ?? false} onChange={(e)=>setValue("safety_features_exempt", e.target.checked)}/>
              Exempt safety AI (position review)
            </label>
          </div>
          {!isOwner && <div className="text-xs text-muted-foreground mt-4">Owner-editable only. You can see live usage on the right.</div>}
        </div>
        <div className="grid-cell space-y-4">
          <div className="label-cap">Current Period Usage</div>
          <Bar used={summary.user_day} cap={cfg.daily_per_user_usd} label="Your daily" testid="usage-user-day"/>
          <Bar used={summary.user_month} cap={cfg.monthly_per_user_usd} label="Your monthly" testid="usage-user-month"/>
          <Bar used={summary.workspace_day} cap={cfg.daily_workspace_usd} label="Workspace daily" testid="usage-ws-day"/>
          <Bar used={summary.workspace_month} cap={cfg.monthly_workspace_usd} label="Workspace monthly" testid="usage-ws-month"/>
          <div className="pt-2 text-[11px] text-muted-foreground">
            Warning tiers: <span className="text-signal-yellow mono">75% info</span> · <span className="text-signal-red/80 mono">90% high</span> · <span className="pnl-down mono">100% blocked</span>. Safety AI (position reviews) is exempt when the flag is on.
          </div>
        </div>
      </div>
      {isOwner && <SaveBar dirty={dirty} saving={saving} savedAt={savedAt} errors={errors} onSave={save} onDiscard={discard} testId="save-bar-cost" />}
    </>
  );
}
